import {
  Box,
  Checkbox,
  Flex,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { ReactNode, useEffect, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Button } from './Button';
import { Modal } from './Modal';

export type SelectPersonRow = {
  id: string;
  name: string;
  role: string;
  checked: boolean;
  onToggle: () => void;
  ariaLabel?: string;
  /** Escape hatch for bespoke rows (e.g. an inline role input); overrides the default row. */
  renderCustom?: () => ReactNode;
};

export type SelectResultRow = { id: string; name: string; email?: string; onAdd: () => void };

export type SelectPeopleSection = {
  key: string;
  title: string;
  addLabel: string;
  searchPlaceholder: string;
  noneLabel: string;
  addAsWord?: string;
  allowFreeText?: boolean;
  requiredMessage?: string;
  rows: SelectPersonRow[];
  getResults: (search: string) => SelectResultRow[];
  onAddFreeText?: (typed: string) => void;
};

type SelectPeopleModalProps = {
  opened: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  confirmLabel: string;
  searchConfirmLabel?: string;
  isSaving?: boolean;
  confirmDisabled?: boolean;
  sections: SelectPeopleSection[];
  validationTriggered?: boolean;
  onConfirm: () => void;
  size?: number;
};

const addLinkStyle: React.CSSProperties = {
  color: 'var(--mantine-color-brand-6)',
  fontSize: 12,
  fontWeight: 600,
  textDecorationLine: 'underline',
  textUnderlineOffset: 2,
};

const addResultStyle: React.CSSProperties = {
  color: 'var(--mantine-color-brand-6)',
  fontSize: 12,
  fontWeight: 600,
};

const boxStyle: React.CSSProperties = {
  borderRadius: 16,
  border: '1px solid var(--mantine-color-gray-1)',
  padding: 8,
};

function PersonRow({ row }: { row: SelectPersonRow }) {
  if (row.renderCustom) return <>{row.renderCustom()}</>;
  return (
    <Flex align="center" gap="sm" py="xxxs">
      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10" style={{ width: 160 }}>
        {row.name}
      </Text>
      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6" style={{ flex: 1 }}>
        {row.role}
      </Text>
      <Checkbox
        checked={row.checked}
        onChange={row.onToggle}
        aria-label={row.ariaLabel ?? row.name}
      />
    </Flex>
  );
}

function AddPill({ label, onAdd }: { label: string; onAdd: () => void }) {
  return (
    <UnstyledButton
      style={{ ...addResultStyle, position: 'absolute', top: 4, right: 4 }}
      onClick={onAdd}
    >
      <Flex align="center" gap={2}>
        {label}
        <IconPlus size={16} stroke={2.5} color="var(--mantine-color-brand-6)" />
      </Flex>
    </UnstyledButton>
  );
}

export function SelectPeopleModal({
  opened,
  onClose,
  title,
  subtitle,
  confirmLabel,
  searchConfirmLabel = 'Click done before saving',
  isSaving = false,
  confirmDisabled = false,
  sections,
  validationTriggered = false,
  onConfirm,
  size = 440,
}: SelectPeopleModalProps) {
  const [openSectionKey, setOpenSectionKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (opened) return;
    setOpenSectionKey(null);
    setSearch('');
  }, [opened]);

  const closeSearch = () => {
    setOpenSectionKey(null);
    setSearch('');
  };

  const handleClose = () => {
    closeSearch();
    onClose();
  };

  const openSection = sections.find(section => section.key === openSectionKey);

  if (openSection) {
    const results = openSection.getResults(search);
    const showEmptyAdd =
      !!openSection.allowFreeText && search.trim().length > 0 && results.length === 0;

    return (
      <Modal opened={opened} onClose={handleClose} size={size} title={title}>
        <Box style={boxStyle}>
          <Flex align="center" justify="space-between">
            <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
              {openSection.title}
            </Text>
            <UnstyledButton style={addResultStyle} onClick={closeSearch}>
              Done
            </UnstyledButton>
          </Flex>

          <Stack gap="xxxs" mt={10}>
            {openSection.rows.map(row => (
              <PersonRow key={row.id} row={row} />
            ))}
          </Stack>

          <TextInput
            mt="xs"
            value={search}
            onChange={event => setSearch(event.currentTarget.value)}
            placeholder={openSection.searchPlaceholder}
            leftSection={<IconSearch size={16} />}
            leftSectionWidth={32}
            styles={{ input: { height: 36, minHeight: 36, borderRadius: 12 } }}
          />

          <Box mt={6}>
            <ScrollArea.Autosize mah={260} type="auto">
              <Stack gap="xxs" px="xs" py="xxs">
                {results.map(result => (
                  <Box key={result.id} style={{ position: 'relative' }}>
                    <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
                      {result.name}
                    </Text>
                    {result.email && (
                      <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7" mt="xxxs">
                        {result.email}
                      </Text>
                    )}
                    <AddPill
                      label="Add"
                      onAdd={() => {
                        result.onAdd();
                        setSearch('');
                      }}
                    />
                  </Box>
                ))}

                {!openSection.allowFreeText && results.length === 0 && (
                  <Text size="sm" c="gray.6">
                    {openSection.noneLabel}
                  </Text>
                )}

                {showEmptyAdd && (
                  <Box style={{ position: 'relative' }}>
                    <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
                      {openSection.noneLabel}
                    </Text>
                    {openSection.addAsWord && (
                      <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7" mt="xxxs">
                        But you can still add as a {openSection.addAsWord}
                      </Text>
                    )}
                    <AddPill
                      label={`Add ${search.trim()}`}
                      onAdd={() => {
                        openSection.onAddFreeText?.(search.trim());
                        setSearch('');
                      }}
                    />
                  </Box>
                )}
              </Stack>
            </ScrollArea.Autosize>
          </Box>
        </Box>

        <Box mt="lg">
          <Button fullWidth disabled>
            {searchConfirmLabel}
          </Button>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal opened={opened} onClose={handleClose} size={size} title={title}>
      {subtitle && (
        <Text size="sm" c="dimmed" mb="lg">
          {subtitle}
        </Text>
      )}
      <Stack gap="lg">
        {sections.map(section => (
          <Box key={section.key}>
            <Flex align="center" justify="space-between" px="xxs" pb={10}>
              <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
                {section.title}
              </Text>
              <UnstyledButton style={addLinkStyle} onClick={() => setOpenSectionKey(section.key)}>
                {section.addLabel}
              </UnstyledButton>
            </Flex>

            <Stack gap="xxxs" p="xxs">
              {section.rows.map(row => (
                <PersonRow key={row.id} row={row} />
              ))}
            </Stack>

            {validationTriggered && section.requiredMessage && (
              <Text size="sm" fw={FONT_WEIGHTS.regular} c="error.6" mt="xxs">
                {section.requiredMessage}
              </Text>
            )}
          </Box>
        ))}
      </Stack>

      <Box mt="lg">
        <Button
          fullWidth
          onClick={onConfirm}
          loading={isSaving}
          disabled={isSaving || confirmDisabled}
        >
          {confirmLabel}
        </Button>
      </Box>
    </Modal>
  );
}
